// useSiteData — everything one space's site can show, from one request.
//
// The data comes from GET /api/site/<slug> (api/site/[slug].js), which reads exactly what a
// signed-out visitor may read — public items, through RLS — already narrowed to what the site
// reaches, with cache headers a CDN can serve from. The router starts that request before React
// mounts (siteFetch.js), so by the time this hook runs it is usually already answered.
//
// A visit to a site never signs anyone in: no anonymous account is minted per visitor, and the
// browser makes no Supabase call at all.

import { useEffect, useState } from 'react';
import { fetchSite } from './siteFetch.js';

const EMPTY = { rows: [], typesById: new Map(), fieldNames: {}, spaces: {}, origin: null };

/**
 * @param {string} spaceSlug
 * @returns {{ status: 'loading'|'ready'|'missing'|'error', siteRow?: object, rows: object[],
 *   typesById: Map, fieldNames: Record<string, string|null>, spaces: Record<string, string>, origin: string|null,
 *   error?: Error }}
 *   `spaces` maps each space whose items the site shows to its slug, for addresses.
 *   `origin` is the primary site's own domain, when it has one: what a Share button hands on.
 *   `missing` means the space has no site a visitor can see — no such space, or no Site item
 *   switched on — which wants "no site here" rather than "something went wrong".
 */
export function useSiteData(spaceSlug) {
  const [state, setState] = useState({ status: 'loading', ...EMPTY });

  useEffect(() => {
    let cancelled = false;
    if (!spaceSlug) return undefined;
    fetchSite(spaceSlug).then((data) => {
      if (cancelled) return;
      if (data.missing) { setState({ status: 'missing', ...EMPTY }); return; }
      const rows = Array.isArray(data.rows) ? data.rows : [];
      const siteRow = rows.find((r) => r.id === data.siteId);
      if (!siteRow) { setState({ status: 'missing', ...EMPTY }); return; }
      setState({
        status: 'ready', siteRow, rows, typesById: new Map((data.types || []).map((t) => [t.id, t])),
        fieldNames: data.fields && typeof data.fields === 'object' ? data.fields : {},
        spaces: data.spaces && typeof data.spaces === 'object' ? data.spaces : {},
        origin: typeof data.origin === 'string' ? data.origin : null,
      });
    }).catch((error) => {
      if (!cancelled) setState({ status: 'error', ...EMPTY, error });
    });
    return () => { cancelled = true; };
  }, [spaceSlug]);

  return state;
}
